/**
 * /operations/payroll/wps — Session 3B PR 14.
 * Owning agent: Rawatib. Generates SIF file format for Mudad WPS.
 */

import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Download, FileText, ShieldCheck } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { WpsRunItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

function _statusToneClass(s: WpsRunItem["submission_status"]): string {
  if (s === "accepted") return "bg-emerald-100 text-emerald-800";
  if (s === "rejected") return "bg-rose-100 text-rose-800";
  if (s === "submitted") return "bg-brand-blue/15 text-brand-deep";
  return "bg-amber-100 text-amber-800";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function WpsMudadOutputPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();

  const list = useQuery({
    queryKey: ["session3b-wps", legalEntityId],
    queryFn: () => session3bApi.listWpsRuns(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: WpsRunItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const unsubmitted = items.filter((r) => r.submission_status === "draft").length;
    const submitted = items.filter((r) => r.submission_status === "submitted" || r.submission_status === "accepted").length;
    return {
      total: items.length,
      attention: unsubmitted,
      critical: 0,
      submitted,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("payroll.wps.title", "WPS / Mudad Output")}
          subtitle={t(
            "payroll.wps.subtitle",
            "Generate the Wage Protection System SIF file per period. Mutaba'aa pre-validates. Live Mudad API: pending M3X.GOV — today: manual portal upload.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-text-tertiary">
            {t(
              "payroll.wps.hint",
              "One SIF per posted period. Download → upload to mol.gov.sa / Mudad portal → mark submitted.",
            )}
          </p>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "payroll.wps.cta.generate_note",
                  "Generate SIF File for current period — triggers Mutaba'aa pre-validation then downloads the .txt per Mudad spec.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Download className="h-4 w-4" />
            {t("payroll.wps.cta.generate", "Generate SIF File")}
          </button>
        </div>

        {counts.attention > 0 && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<FileText className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "payroll.wps.suggestion.unsubmitted_headline",
              `${counts.attention} SIF file${counts.attention === 1 ? "" : "s"} generated but not yet submitted.`,
            )}
            body={t(
              "payroll.wps.suggestion.unsubmitted_body",
              "Mudad deadlines vary by month-end. Download the file and upload to the portal; mark submitted here when done.",
            )}
            primaryAction={{
              label: t("payroll.wps.suggestion.go_first", "Jump to oldest"),
              onClick: () => {
                const first = document.querySelector("[data-wps-row='true']");
                if (first) first.scrollIntoView({ behavior: "smooth" });
              },
            }}
            suggestionKey="wps-unsubmitted"
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<ShieldCheck className="h-4 w-4" />}
            severity="info"
            headline={t(
              "payroll.wps.suggestion.empty_headline",
              "No SIF files generated yet.",
            )}
            body={t(
              "payroll.wps.suggestion.empty_body",
              "Rawatib notes: hit Generate SIF File above to render the current period.",
            )}
            primaryAction={{
              label: t("payroll.wps.suggestion.open_period_lock", "Open Period Lock"),
              onClick: () => window.location.assign("/operations/payroll/period-lock"),
            }}
            suggestionKey="wps-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("payroll.wps.col.period", "Period")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.generated", "Generated")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.hash", "SIF hash")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.employees", "Employees")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.total", "Total (SAR)")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("payroll.wps.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.run_id} data-wps-row="true" className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2 text-text-secondary">{row.period_label}</td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.generated_at}</span>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "payroll.wps.trace.hash",
                              "SIF hash = SHA-256 of (run_id : pay_period_end : total). Deterministic — re-generation with no input changes produces the same hash, so HR can verify integrity before Mudad upload.",
                            ),
                          )
                        }
                      >
                        <span className="font-mono text-[10px] text-text-tertiary">
                          {row.sif_file_hash ?? "—"}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.employees_count}</td>
                    <td className="px-3 py-2 font-mono text-xs">{_fmtSar(row.total_amount_sar)}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.submission_status)}`}
                      >
                        {row.submission_status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() =>
                            showAlertTrace(
                              t("payroll.wps.action.download_note", "Download the .txt SIF file."),
                            )
                          }
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          <Download className="h-3 w-3" />
                          {t("payroll.wps.action.download", "Download")}
                        </button>
                        {row.submission_status === "draft" && (
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t("payroll.wps.action.mark_submitted_note", "Mark this SIF as submitted manually."),
                              )
                            }
                            className="inline-flex items-center gap-1 rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            {t("payroll.wps.action.mark_submitted", "Mark submitted")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="rounded-2xl border border-dashed border-border-subtle bg-surface-sunken/40 p-3 text-[10px] text-text-tertiary">
          {t(
            "payroll.wps.gov_note",
            "Note: live API submission to Mudad lands in the M3X.GOV pack. Today every SIF is manually uploaded to the Mudad portal by HR; mark submitted here once accepted.",
          )}
        </div>
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: 0,
            top_kind: t("payroll.wps.title", "WPS / Mudad Output"),
            window_label: `${counts.submitted} submitted · ${counts.attention} pending`,
          }}
        />
      </aside>
    </div>
  );
}
